import React from 'react';
import { Table, Button, Modal, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';

function ShoppingCart({ cart, setCart }) {
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [error, setError] = useState('');

  // Add function for dynamic card heights
  const getCardStyle = (rows) => {
    // If content is empty, make it very compact
    if (!rows || rows.length === 0) {
      return { 
        maxHeight: 'none',
        minHeight: 'auto',
        height: 'auto'
      };
    }
    
    // For small number of rows, make it compact with no extra space
    if (rows.length <= 5) {
      return { 
        maxHeight: 'none',
        minHeight: 'auto',
        height: 'auto'
      };
    }
    
    // For medium number of rows (6-10), allow a bit more height but still compact
    if (rows.length <= 10) {
      return { 
        maxHeight: '400px',
        overflowY: 'auto',
        minHeight: 'auto'
      };
    }
    
    // For many rows, limit height and add scroll
    return { 
      maxHeight: '500px', 
      overflowY: 'auto',
      minHeight: 'auto'
    };
  };

  const safeUpdateCart = (updater) => {
    setCart(prevCart => {
      const newCart = typeof updater === 'function' ? updater(prevCart) : updater;
      try {
        sessionStorage.setItem('cart', JSON.stringify(newCart));
      } catch (error) {
        console.error('Error saving cart to sessionStorage:', error);
      }
      return newCart;
    });
  };

  const handleRemoveItem = (idLevels) => {
    safeUpdateCart(prevCart => prevCart.filter(item => item.idLevels !== idLevels));
  };

  const handleQuantityChange = (idLevels, newValue) => {
    const item = cart.find(item => item.idLevels === idLevels);
    if (!item) return;

    const availQty = Number(item.availQty) || 0;
    const newQuantity = parseInt(newValue) || 0;

    // If quantity is 0 or negative, remove the item
    if (newQuantity <= 0) {
      handleRemoveItem(idLevels);
      return;
    }

    // If quantity exceeds available, show error and keep previous valid quantity
    if (newQuantity > availQty) {
      setError(`Cannot exceed available quantity of ${availQty} for ${item.itemName}`);
      setTimeout(() => setError(''), 3000);
      
      // Update cart with the maximum allowed quantity
      safeUpdateCart(prevCart => prevCart.map(cartItem => 
        cartItem.idLevels === idLevels
          ? { ...cartItem, quantity: availQty }
          : cartItem
      ));
      return;
    }

    // Update with the new valid quantity
    safeUpdateCart(prevCart => prevCart.map(cartItem => 
      cartItem.idLevels === idLevels
        ? { ...cartItem, quantity: newQuantity }
        : cartItem
    ));
  };

  const handleClearCart = () => {
    safeUpdateCart([]);
    setShowClearConfirmation(false);
  };

  // Calculate cart totals
  const cartTotals = cart.reduce((acc, item) => {
    const ppu = Number(item.ppu) || 1;
    const quantity = Number(item.quantity) || 0;
    return {
      totalQuantity: acc.totalQuantity + quantity,
      totalPieces: acc.totalPieces + (quantity * ppu)
    };
  }, { totalQuantity: 0, totalPieces: 0 });

  return (
    <div className="shopping-cart">
      {error && (
        <div className="alert alert-danger mb-3">
          {error}
        </div>
      )}
      
      {cart.length === 0 ? (
        <p>Your cart is empty.</p>
      ) : (
        <>
          <div className="table-responsive" style={getCardStyle(cart)}>
            <Table striped bordered hover className="mb-3">
              <thead style={{ position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
                <tr>
                  <th style={{ minWidth: '100px' }}>Lot ID</th>
                  <th style={{ minWidth: '100px' }}>Item #</th>
                  <th style={{ minWidth: '200px' }}>Item</th>
                  <th style={{ minWidth: '200px' }}>Notes</th>
                  <th style={{ minWidth: '80px', textAlign: 'right' }}>PPU</th>
                  <th style={{ minWidth: '100px', textAlign: 'right' }}>Total Pieces</th>
                  <th style={{ minWidth: '100px', textAlign: 'right' }}>Available</th>
                  <th style={{ minWidth: '100px', textAlign: 'right' }}>Quantity</th>
                  <th style={{ width: '80px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cart.map(item => {
                  const availQty = Number(item.availQty) || 0;
                  const ppu = Number(item.ppu) || 1;
                  const totalPieces = ppu * item.quantity;
                  return (
                    <tr key={item.idLevels}>
                      <td>{item.idLevels}</td>
                      <td>{item.itemNumber}</td>
                      <td>{item.itemName}</td>
                      <td>{item.notes}</td>
                      <td className="text-end">{ppu.toLocaleString()}</td>
                      <td className="text-end">{totalPieces.toLocaleString()}</td>
                      <td className="text-end">{availQty.toLocaleString()}</td>
                      <td className="text-end">
                        <div className="d-flex justify-content-end">
                          <Form.Control
                            type="number"
                            min="1"
                            max={availQty}
                            value={item.quantity}
                            onChange={(e) => handleQuantityChange(item.idLevels, e.target.value)}
                            onBlur={(e) => handleQuantityChange(item.idLevels, e.target.value)}
                            style={{ width: '80px', textAlign: 'right' }}
                            isInvalid={item.quantity > availQty}
                          />
                        </div>
                        {item.quantity > availQty && (
                          <Form.Control.Feedback type="invalid">
                            Exceeds available quantity
                          </Form.Control.Feedback>
                        )}
                      </td>
                      <td className="text-center">
                        <Button variant="danger" size="sm" onClick={() => handleRemoveItem(item.idLevels)}>
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="5" className="text-end fw-bold">Totals:</td>
                  <td className="text-end fw-bold">{cartTotals.totalPieces.toLocaleString()}</td>
                  <td></td>
                  <td className="text-end fw-bold">{cartTotals.totalQuantity.toLocaleString()}</td>
                  <td></td>
                </tr>
              </tfoot>
            </Table>
          </div>
          <div className="d-flex justify-content-end">
            <Button 
              variant="danger" 
              size="sm"
              onClick={() => setShowClearConfirmation(true)}
            >
              Clear Cart
            </Button>
          </div>

          <Modal 
            show={showClearConfirmation} 
            onHide={() => setShowClearConfirmation(false)}
          >
            <Modal.Header closeButton>
              <Modal.Title>Clear Cart</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              Are you sure you want to clear all items from your cart?
            </Modal.Body>
            <Modal.Footer>
              <Button 
                variant="secondary" 
                onClick={() => setShowClearConfirmation(false)}
              >
                Cancel
              </Button>
              <Button 
                variant="danger" 
                onClick={handleClearCart}
              >
                Clear Cart
              </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}
      
      <style jsx>{`
        .table-responsive {
          scrollbar-width: thin;
          scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
        }

        .table-responsive::-webkit-scrollbar {
          width: 6px;
        }

        .table-responsive::-webkit-scrollbar-track {
          background: transparent;
        }

        .table-responsive::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.2);
          border-radius: 3px;
        }

        .table-responsive::-webkit-scrollbar-thumb:hover {
          background-color: rgba(0, 0, 0, 0.3);
        }

        .table thead th {
          border-bottom: 2px solid #dee2e6;
          font-weight: 600;
          font-size: 0.875rem;
          padding: 0.5rem;
        }

        .table td {
          font-size: 0.875rem;
          padding: 0.5rem;
          vertical-align: middle;
        }

        .table tfoot td {
          font-size: 0.875rem;
          padding: 0.5rem;
          border-top: 2px solid #dee2e6;
        }
      `}</style>
    </div>
  );
}

export default ShoppingCart;
